import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../../utils/authStorage';
import { GlobalLoading, showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import { generateYearRange } from '../../../../../Custom/Custom';
import asiTitle from '../../../../../asi.json';

const getAuthConfig = () => {
  if (!hasAuthSession()) return null;
  return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
};

const BlockEReport = () => {
  const navigate = useNavigate();
  
  // State for report data
  const [reportData, setReportData] = useState([]);
  const [footerData, setFooterData] = useState({
    bonus: 0,
    pfAndOtherFunds: 0,
    welfareExpenses: 0,
    manufacturingDays: 0,
    nonManufacturingDays: 0,
    totalWorkingDays: 0,
    totalCostOfProduction: 0
  });
  const [loading, setLoading] = useState(true);

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};
    
    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '2022-23',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  const storedLanguage = localStorage.getItem('language') || 'en';

  // Fetch data function
  const fetchBlockEData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      setLoading(false);
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0]
    };
    
    showLoading(true);
    setLoading(true);
    
    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        setLoading(false);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockEForm`, requestPayload, config);
      const { status, data } = response.data;
      
      if (status && data && data.blockEData && data.blockEData.length > 0) {
        console.log('Block E API Response:', data.blockEData);
        // Map API response to report format
        const mappedData = mapApiDataToReport(data.blockEData);
        console.log('Mapped Report Data:', mappedData.reportData);
        console.log('Mapped Footer Data:', mappedData.footerData);
        console.log('Report Data length:', mappedData.reportData.length);
        
        setReportData(mappedData.reportData);
        
        // Extract footer data
        setFooterData(mappedData.footerData);
        
        console.log('Block E data loaded successfully:', data.blockEData.length, 'items');
      } else {
        setReportData([]);
        toast.warning('No Block E data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      }
    } catch (error) {
      console.error('API Error:', error);
      
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
      setReportData([]);
    } finally {
      showLoading(false);
      setLoading(false);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear]);

  // Map API response data to report format
  const mapApiDataToReport = (blockEData) => {
    // Detect Part A categories dynamically - filter out header rows
    // Find the range of Part A categories by excluding known header and Part B categories
    // Header categories: 38, 48, 95, 106 (Part A headers)
    // Part B categories: 49-56, 107-114 (footer items)
    
    // Filter Part A data: exclude headers (38, 48, 95, 106) and Part B (49-56, 107-114)
    const partAData = blockEData.filter(item => {
      const id = item.catagoryId;
      // Exclude header rows
      if ([38, 48, 95, 106].includes(id)) return false;
      // Exclude Part B footer categories
      if ((id >= 49 && id <= 56) || (id >= 107 && id <= 114)) return false;
      // Include all other categories (should be Part A)
      return true;
    });

    // Sort by catagoryId itself (ascending order) - no hardcoded arrays needed
    const sortedPartAData = partAData.sort((a, b) => a.catagoryId - b.catagoryId);

    const reportData = sortedPartAData.map((item, index) => ({
      id: index + 1,
      name: item.categoryOfStaff,
      manufacturingMandays: parseFloat(item.manufacturing || 0),
      nonManufacturingMandays: parseFloat(item.nonmanufacturing || 0),
      totalMandays: parseFloat(item.total || 0),
      avgPersons: parseFloat(item.avgNoPersonsWorked || 0),
      mandaysPaid: parseFloat(item.noMandaysPaid || 0),
      wagesSalaries: parseFloat(item.wagesOrSalaries || 0)
    }));

    // Extract Part B footer data dynamically by category name (works for any year format)
    const bonusItem = blockEData.find(item => 
      item.categoryOfStaff && item.categoryOfStaff.toLowerCase().includes('bonus')
    );
    const pfItem = blockEData.find(item => 
      item.categoryOfStaff && (
        item.categoryOfStaff.toLowerCase().includes('provident fund') ||
        item.categoryOfStaff.toLowerCase().includes('pf')
      )
    );
    const welfareItem = blockEData.find(item => 
      item.categoryOfStaff && item.categoryOfStaff.toLowerCase().includes('welfare')
    );
    const manufacturingDaysItem = blockEData.find(item => 
      item.categoryOfStaff && item.categoryOfStaff.toLowerCase().includes('manufacturing days')
    );
    const nonManufacturingDaysItem = blockEData.find(item => 
      item.categoryOfStaff && item.categoryOfStaff.toLowerCase().includes('non-manufacturing days')
    );
    const totalWorkingDaysItem = blockEData.find(item => 
      item.categoryOfStaff && (
        item.categoryOfStaff.toLowerCase().includes('total (i+ii)') ||
        item.categoryOfStaff.toLowerCase().includes('total (i+2)')
      )
    );
    const totalCostItem = blockEData.find(item => 
      item.categoryOfStaff && item.categoryOfStaff.toLowerCase().includes('total cost of production')
    );

    const footerData = {
      bonus: parseFloat(bonusItem?.wagesOrSalaries || 0),
      pfAndOtherFunds: parseFloat(pfItem?.wagesOrSalaries || 0),
      welfareExpenses: parseFloat(welfareItem?.wagesOrSalaries || 0),
      manufacturingDays: parseFloat(manufacturingDaysItem?.wagesOrSalaries || 0),
      nonManufacturingDays: parseFloat(nonManufacturingDaysItem?.wagesOrSalaries || 0),
      totalWorkingDays: parseFloat(totalWorkingDaysItem?.wagesOrSalaries || 0),
      totalCostOfProduction: parseFloat(totalCostItem?.wagesOrSalaries || 0)
    };

    return {
      reportData,
      footerData
    };
  };

  useEffect(() => {
    fetchBlockEData();
  }, [fetchBlockEData]);

  const formatNumber = (num) => {
    if (!num || num === 0) return '0';
    return parseFloat(num).toFixed(0);
  };

  // Memoized table headers
  const tableHeaders = useMemo(() => {
    const isHindi = storedLanguage === 'hi';
    return {
      slNo: isHindi ? asiTitle.hi.SL_NO : asiTitle.en.SL_NO,
      dslNo: isHindi ? asiTitle.hi.DSL_NO : asiTitle.en.DSL_NO,
      surveyYear: isHindi ? asiTitle.hi.SERVEY_YEAR : asiTitle.en.SERVEY_YEAR,
      blockETitle: isHindi ? asiTitle.hi.BLOCK_E_TITLE : asiTitle.en.BLOCK_E_TITLE
    };
  }, [storedLanguage]);

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      
      <style>
        {`
          .report-container {
            background-color: #fff;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .report-title {
            background-color: #fff;
            color: #333;
            font-size: 18px;
            font-weight: bold;
            padding: 15px 20px;
            margin: -20px -20px 20px -20px;
            border-radius: 6px 6px 0 0;
            border-bottom: 2px solid #333;
            text-align: center;
          }
          .report-info {
            background-color: #f8f9fa;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
            border-left: 4px solid #333;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 12px;
          }
          .report-table th,
          .report-table td {
            border: 1px solid #333;
            padding: 6px 4px;
            text-align: center;
            vertical-align: middle;
          }
          .report-table th {
            background-color: #f5f5f5;
            color: #333;
            font-weight: bold;
            font-size: 11px;
          }
          .report-table td {
            background-color: #fff;
          }
          .report-table .category-name {
            text-align: left;
            font-weight: 500;
            padding-left: 8px;
          }
          .report-table .bold-text {
            font-weight: bold;
          }
          .report-table .empty-cell {
            background-color: #fafafa;
          }
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 40px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
          .part-label {
            font-weight: bold;
            text-align: left;
            padding: 8px;
            background-color: #f0f0f0;
          }
         
          @media print {
            .no-print { display: none !important; }
            .report-container { border: none; margin: 0; padding: 0; }
            .report-title { margin: 0; border-radius: 0; }
            .report-info { margin: 0 0 20px 0; }
            .btn_orange { display: none !important; }
          }
        `}
      </style>

      <div className="container-fluid py-3">
        {/* Report Content */}
        <div className="report-container">
         
          <div className="report-title">
            <div dangerouslySetInnerHTML={{ __html: tableHeaders.blockETitle }} />
          </div>
          
          <div className="report-info">
            <div className="row">
              <div className="col-md-6">
                <strong>{tableHeaders.dslNo}:</strong> {blockInfo.dslNo || 'Not Available'}
              </div>
              <div className="col-md-6">
                <strong>{tableHeaders.surveyYear}:</strong> {blockInfo.surveyYear || 'Not Available'}
              </div>
            </div>
          </div>
          
          {reportData.length > 0 ? (
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th rowSpan="2" style={{ width: '5%' }}>SI No</th>
                    <th rowSpan="2" style={{ width: '35%' }}>Category of staff</th>
                    <th colSpan="3" style={{ width: '20%' }}>Mandays Worked</th>
                    <th rowSpan="2" style={{ width: '12%' }}>Average Number of persons worked</th>
                    <th rowSpan="2" style={{ width: '12%' }}>No. of Mandays Paid for</th>
                    <th rowSpan="2" style={{ width: '16%' }}>Wages/ Salaries (in Rs.)</th>
                  </tr>
                  <tr>
                    <th>Manufacturing</th>
                    <th>Non Manufacturing</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Part A: details for each category of staff */}
                  <tr>
                    <td colSpan="8" className="part-label">Part A: details for each category of staff</td>
                  </tr>
                  {reportData.map((row, index) => {
                    // Check if this row should be bold based on category name (works for both 2022 and 2023 formats)
                    const isBold = row.name.includes('Sub Total') || 
                                  row.name.includes('Total Workers') || 
                                  row.name.includes('Total employees');
                    
                    return (
                      <tr key={row.id}>
                        <td>{row.id}</td>
                        <td className={`category-name ${isBold ? 'bold-text' : ''}`}>{row.name}</td>
                        <td>{formatNumber(row.manufacturingMandays)}</td>
                        <td>{formatNumber(row.nonManufacturingMandays)}</td>
                        <td>{formatNumber(row.totalMandays)}</td>
                        <td>{formatNumber(row.avgPersons)}</td>
                        <td>{formatNumber(row.mandaysPaid)}</td>
                        <td>{formatNumber(row.wagesSalaries)}</td>
                      </tr>
                    );
                  })}
                  
                  {/* Part B: some details for all categories of staff combined */}
                  <tr>
                    <td colSpan="8" className="part-label">Part B: some details for all categories of staff combined</td>
                  </tr>
                  <tr>
                    <td>10</td>
                    <td className="category-name">Bonus (in Rs.)</td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td>{formatNumber(footerData.bonus)}</td>
                  </tr>
                  <tr>
                    <td>11</td>
                    <td className="category-name">Contribution to Provident Fund and Other Funds (in Rs.)</td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td>{formatNumber(footerData.pfAndOtherFunds)}</td>
                  </tr>
                  <tr>
                    <td>12</td>
                    <td className="category-name">Workman and Staff Welfare Expenses (in Rs.)</td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td>{formatNumber(footerData.welfareExpenses)}</td>
                  </tr>
                  <tr>
                    <td rowSpan="3">13</td>
                    <td rowSpan="3" className="category-name" style={{ verticalAlign: 'middle' }}>
                      Number of Working Days
                    </td>
                    <td colSpan="5" className="category-name" style={{ textAlign: 'left', paddingLeft: '8px' }}>
                      1. Manufacturing days
                    </td>
                    <td>{formatNumber(footerData.manufacturingDays)}</td>
                  </tr>
                  <tr>
                    <td colSpan="5" className="category-name" style={{ textAlign: 'left', paddingLeft: '8px' }}>
                      2. Non-manufacturing days
                    </td>
                    <td>{formatNumber(footerData.nonManufacturingDays)}</td>
                  </tr>
                  <tr>
                    <td colSpan="5" className="category-name" style={{ textAlign: 'left', paddingLeft: '8px' }}>
                      3. Total (1+2)
                    </td>
                    <td>{formatNumber(footerData.totalWorkingDays)}</td>
                  </tr>
                  <tr>
                    <td>14</td>
                    <td className="category-name">
                      Total Cost of Production (in Rs.): <br/>
                      <span style={{ fontSize: '10px', fontStyle: 'italic', color: '#666' }}>
                        [Entry in col. 8 of item 9, 10,11 & 12 of Bl. E (+) entry in col. 3 of item (1 to 10) of Bl. F (+) entry in col.6 of item 23 of Bl. H (+) entry in col. 6 of item 7 of Bl. I ]
                      </span>
                    </td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td className="empty-cell"></td>
                    <td>{formatNumber(footerData.totalCostOfProduction)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : !loading ? (
            <div className="no-data">No Block E data available for this DSL No.</div>
          ) : null}
        </div>

        {/* Action Buttons */}
        <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/report-selection')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/blockFreport')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlockEReport;
